import {
	LANG,
	PAGE_KEYS,
	CONTROLS_BY_TYPE,
	CONTROLS_STATS,
	CONTROLS_STAT_GROUP_KEYS,
	CONTROLS_STATS_GROUPED,
	CONTROLS_YEARLY_STAT_KEYS,
	CONTROLS_STAT_YEARS,
	CONTROLS_YEAR_INDIVIDUAL_KEYS,
	STAT_EXTENTS,
	STAT_AGE_GROUPS,
	STAT_UNITS,
	STATS_CA_ONLY,
	YEAR_BUCKETS,
} from "./constants";
import { ControlsGeoType, ControlsStatGroupType, ControlsStatType, ControlsViewType, ControlsYearType } from "./types";
import { getValue, replaceStringWithParams } from "./helpers"

// Getters
/** Gets a array of page keys */
export const getPageKeys = () => PAGE_KEYS;
/** Gets an array of control types */
export const getControlTypes = () => Object.keys(CONTROLS_BY_TYPE);
/** Gets an array of options for the given control type */
export const getControlOptionsByType = (type: string) => getValue(type, CONTROLS_BY_TYPE);
/** Gets an array of stat groups */
export const getControlStatGroups = () => CONTROLS_STAT_GROUP_KEYS;
/** Get an array of all stats of the given view (regardless of group) */
export const getAllStatsOfView = (view: ControlsViewType) => Object.values(getValue(view, CONTROLS_STATS)).flat() as ControlsStatType[];
/** Gets nested control stats with an group */
export const getControlStats = (type: ControlsViewType, group: ControlsStatGroupType, subGroup?: string) => (
	subGroup
		? getValue(subGroup, getValue(group, getValue(type, CONTROLS_STATS_GROUPED)))
		: getValue(group, getValue(type, CONTROLS_STATS_GROUPED))
);
/** Get the unit of this stat */
export const getUnit = (key: string) => getValue(key, STAT_UNITS);
/** Get extents of a specific stat for the given geo and view type */
export const getExtents = (geo: ControlsGeoType, view: ControlsViewType, stat: ControlsStatType) => {
	const extents = getValue(stat, getValue(view, getValue(geo, STAT_EXTENTS))) ?? [0, 1]
	const steps = 6;
	const delta = (extents[1] - extents[0]) / (steps - 1);
	const newExtents = Array.from({
		length: steps
	}, (step, index) =>
		Math.round((extents[0] + index * delta) * 100) / 100
	);
	return newExtents;
};
/** Gets a array of age group keys */
export const getAgeGroups = () => STAT_AGE_GROUPS;
/** Gets a array of individual years selectable in the controls */
export const getIndividualYears = () => CONTROLS_YEAR_INDIVIDUAL_KEYS;
/** Gets the year bucket of a specific year control */
export const getBucketByYear = (year: ControlsYearType) =>
	Object.keys(YEAR_BUCKETS).find(bucket =>
		getValue(bucket, YEAR_BUCKETS)?.some((y: string | number) => y === year) === true
	) ?? "";
export const getYearsOfStat = (view: ControlsViewType, stat: ControlsStatType) =>
	getValue(`${view}_${stat}`, CONTROLS_STAT_YEARS);
export const getLang = (
	...strs: (string | number | object | null | undefined)[]
): string => {
	// If last argument is an object, remove this from string list and use as params
	const params = typeof strs[strs.length - 1] === "object"
		? strs.pop()
		: null;
	// If attempting to get lang for a year (i.e. `year_2024`), just return year
	// if(strs[0] === "year" && strs.length > 1 && !String(strs[1]).includes("_") && strs[1] !== "2050")
	// 	return String(strs[1]);
	// Join all string arguments with underscore to create single key string
	const key = strs.filter(s => s).join("_");
	const langObj: object = LANG;
	// Get lang using key from ./lang.ts object
	const lang = getValue(key, langObj);
	// If params exists in arguments, replace param's keys found in lang string with param's value
	if(params && typeof params === "object") {
		return replaceStringWithParams(lang, params);
	} else {
		return lang;
	}
}
// Checkers
/** Checks if the value is valid within the control type */
export const isValidValueOfType = (type: string, value: any, view?: ControlsViewType) => {
	if(value && type !== "stat") {
		return getControlOptionsByType(type)?.includes(value)
	} else if(view && value && type === "stat" && view) {
		return getAllStatsOfView(view)?.includes(value)
	} else {
		return false;
	}
}
/** Checks if the stat has data across individual years */
export const isStatYearly = (key: ControlsStatType) => CONTROLS_YEARLY_STAT_KEYS.includes(key);
/** Checks if a year is an individual year, e.g. `2019` (opposed to an average range, e.g. `20avg`) */
export const isYearIndividual = (key: ControlsYearType) => CONTROLS_YEAR_INDIVIDUAL_KEYS.includes(key);
/** Checks if this year option disabled within the given control params */
export const isYearOptionDisabled = (
	view: ControlsViewType,
	stat: ControlsStatType,
	year: ControlsYearType
) => !getValue(`${view}_${stat}`, CONTROLS_STAT_YEARS)?.includes(year);
/** Checks if the given stat is only available in California */
export const isStatCaOnly = (key: ControlsStatType) => STATS_CA_ONLY.includes(key);